/**
 * Collection Spotlight (E20-1086)
 *
 * The carousel column beside the feature image: a heading row with prev/next arrow
 * controls, a horizontally scrolling row of reusable Product Cards, and pagination
 * dots below. Navigation snaps one card at a time (arrows, dot clicks, and — on
 * desktop — mouse/pen drag; touch uses native swipe). One dot per reachable scroll
 * position (fewer than the cards, since two are in view); the active dot tracks the
 * left-most card. Arrow controls disable (25% opacity
 * via CSS) at each end. Reduced-motion aware.
 *
 * Reuses the control/disabled logic of the Product Carousel (E20-1087) and the dot
 * logic of the Feature Carousel (E20-1071); it pages a single card at a time so the
 * dots stay in step with the cards.
 *
 * The guard wraps the class too, not just the define(): the section emits this tag per
 * instance, and a page with two of them would otherwise re-declare a top-level binding
 * and throw at parse time, before any guard could run.
 */

if (!customElements.get('collection-spotlight')) {
  class CollectionSpotlight extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.cards = Array.from(this.querySelectorAll('.bfx-collection-spotlight__cell'));
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      if (!this.track) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.ticking = false;
      this.isDown = false;

      this.prev?.addEventListener('click', () => this.page(-1), { signal });
      this.next?.addEventListener('click', () => this.page(1), { signal });
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.scrollToCard(index), { signal });
      });

      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      window.addEventListener('resize', () => this.update(), { passive: true, signal });

      // Mouse / pen drag-to-scroll (touch uses native scrolling).
      this.track.addEventListener('pointerdown', (e) => this.onPointerDown(e), { signal });
      this.track.addEventListener('pointermove', (e) => this.onPointerMove(e), { signal });
      this.track.addEventListener('pointerup', (e) => this.onPointerUp(e), { signal });
      this.track.addEventListener('pointercancel', (e) => this.onPointerUp(e), { signal });
      // Suppress a card link click that's really the end of a drag.
      this.track.addEventListener(
        'click',
        (e) => {
          if (this.dragged) {
            e.preventDefault();
            e.stopPropagation();
            this.dragged = false;
          }
        },
        { capture: true, signal }
      );

      this.update();
    }

    reduceMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // The track's start padding that cards align to (0 here, but read it so the snap
    // math stays correct if a gutter is ever added).
    gutter() {
      return parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
    }

    // One card's width plus the row gap. Cards are uniform, so a single measurement is
    // enough — avoids a per-card layout read on every scroll tick.
    cardOuterWidth() {
      const card = this.cards[0];
      if (!card) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    // Index of the card aligned to the start edge. Snapped scrollLeft for card i is
    // i * cardOuterWidth, so round to the nearest card.
    //
    // We intentionally do NOT force the last index at max scroll: with two cards in
    // view the last card can never be the left-most one, so forcing it there made
    // page(-1) compute a target equal to the current position — the prev arrow was
    // enabled but stalled at the end of the row. Rounding returns the true left-most
    // card so paging (and the dots) work from either end.
    activeIndex() {
      const { scrollLeft } = this.track;
      if (scrollLeft <= 1) return 0;

      const cardOuter = this.cardOuterWidth();
      if (cardOuter <= 0) return 0;
      const index = Math.round(scrollLeft / cardOuter);
      return Math.min(this.cards.length - 1, Math.max(0, index));
    }

    // Page one card at a time so the dots stay in step with the cards.
    page(direction) {
      const target = this.activeIndex() + direction;
      this.scrollToCard(Math.min(this.cards.length - 1, Math.max(0, target)));
    }

    // Aligns a card's start edge to the column (matches the snap point).
    scrollToCard(index) {
      const card = this.cards[index];
      if (!card) return;
      const target =
        card.getBoundingClientRect().left -
        this.track.getBoundingClientRect().left +
        this.track.scrollLeft -
        this.gutter();
      this.track.scrollTo({ left: target, behavior: this.reduceMotion() ? 'auto' : 'smooth' });
    }

    onPointerDown(e) {
      this.dragged = false;
      if (e.pointerType === 'touch' || e.button !== 0) return;
      this.isDown = true;
      this.pointerId = e.pointerId;
      this.startX = e.clientX;
      this.startScroll = this.track.scrollLeft;
    }

    onPointerMove(e) {
      if (!this.isDown || e.pointerId !== this.pointerId) return;
      if (e.buttons === 0) {
        this.onPointerUp(e);
        return;
      }
      const dx = e.clientX - this.startX;
      if (!this.dragged) {
        if (Math.abs(dx) <= 5) return; // not a drag yet — let clicks through to links
        this.dragged = true;
        this.track.setPointerCapture(this.pointerId);
        this.track.classList.add('is-dragging');
      }
      this.track.scrollLeft = this.startScroll - dx;
    }

    onPointerUp(e) {
      if (!this.isDown || e.pointerId !== this.pointerId) return;
      this.isDown = false;
      this.track.classList.remove('is-dragging');
      try {
        this.track.releasePointerCapture(e.pointerId);
      } catch {
        // pointer may already be released or was never captured
      }
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      this.updateControls();
      this.updateDots();
    }

    updateControls() {
      if (!this.prev || !this.next) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      // Nothing to page in a direction → disable (covers "all cards fit": both disabled).
      this.prev.disabled = scrollLeft <= 1;
      this.next.disabled = scrollLeft >= scrollWidth - clientWidth - 1;
    }

    updateDots() {
      if (this.dots.length === 0) return;
      // One dot per reachable scroll position (there are fewer positions than cards
      // because two cards are in view), so clamp to the dot count — the last position
      // lands on the last dot.
      const active = Math.min(this.dots.length - 1, Math.max(0, this.activeIndex()));
      this.dots.forEach((dot, i) => {
        const isActive = i === active;
        dot.classList.toggle('bfx-collection-spotlight__dot--active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }
  }

  customElements.define('collection-spotlight', CollectionSpotlight);
}
